import type Luminary from '../../../..';
import type { TextLineMetrics } from '../metrics/TextLineMetrics';
import { TextEditingManager } from './TextEditingManager';

// https://developer.mozilla.org/en-US/docs/Web/API/Node/nodeType
const NODE_TYPE_TEXT = 3;

enum NodeTraversalDirection {
    FirstChild,
    LastChild,
}

/**
 * Creates a custom named error for Sentry reporting grouping
 */
export class GroupedTextError extends Error {
    constructor(msg: string, name: string, originalError?: Error) {
        super(msg);
        this.name = name;
        if (originalError) {
            this.stack = originalError.stack;
        }
    }
}

/**
 * Start and end indices corresponding to the text property. A tuple containing identical
 * values is considered collapsed.
 */
export type CharTuple = [number, number];

/**
 * Represents a selection. A collapsed selection will have identical nodes and offsets.
 * The type is not to be confused with a "start" and "end"
 */
export type AnchorAndFocus = {
    anchorNode: Node;
    anchorOffset: number;
    focusNode: Node;
    focusOffset: number;
};

export type NodeAndOffset = {
    node: Node;
    offset: number;
};

/**
 * Calculates an updated highlight range that could be shifted, extended,
 * shrunk, and/or removed according to the change made
 *
 * @param netCharDiff The difference between the original text length and the new text length
 * @param changeStartIdx The index where the change took place.
 *  - For any additive change or non-collapsed removal, this is the start of the selection
 *  - For a collapsed removal, this is the start less the amount of characters removed
 *
 * @returns Original reference to highlights if nothing needs to
 *  be changed, else a new reference to a list of updated highlights
 */
export function getUpdatedHighlightRanges(
    netCharDiff: number,
    changeStartIdx: number,
    highlights: Array<Array<number>>,
): Array<[number, number]> {
    const isNetAddition = netCharDiff > 0;
    const updatedHighlights = [];

    let hasChanged = false;
    highlights.forEach((highlightRange) => {
        const [startIdx, endIdx] = highlightRange;

        // If there is a net addition, and the change occured within a range or at the very end (just outside)
        // the range, extend it by the number of inserted characters
        const isAdditionWithinRange =
            isNetAddition && changeStartIdx > startIdx && changeStartIdx <= endIdx;
        if (isAdditionWithinRange) {
            hasChanged = true;
            updatedHighlights.push([startIdx, endIdx + netCharDiff]);
            return;
        }

        // Handle net subtractions that affects a highlight range by shrinking it
        const endAffectedIdx = changeStartIdx + Math.abs(netCharDiff);
        const doesSubtractionAffectRange =
            !isNetAddition && endAffectedIdx > startIdx && changeStartIdx <= endIdx;
        if (doesSubtractionAffectRange) {
            hasChanged = true;

            const numCharsDeletedBeforeRange = Math.max(0, startIdx - changeStartIdx);
            const numCharsDeletedInRange =
                Math.min(endAffectedIdx, endIdx) - Math.max(startIdx, changeStartIdx);

            const newStartHighlightIdx = startIdx - numCharsDeletedBeforeRange;
            const newEndHighlightIdx = endIdx - numCharsDeletedBeforeRange - numCharsDeletedInRange;

            // Only preserve the highlight if it wasn't all deleted
            if (newEndHighlightIdx >= newStartHighlightIdx) {
                updatedHighlights.push([newStartHighlightIdx, newEndHighlightIdx]);
            }

            return;
        }

        // For any range that exists before the net changes, adjust the indices accordingly
        const isAdditionBeforeRange = isNetAddition && changeStartIdx <= startIdx;
        const isSubtractionBeforeRange = !isNetAddition && endAffectedIdx <= startIdx;
        if (isAdditionBeforeRange || isSubtractionBeforeRange) {
            hasChanged = true;
            updatedHighlights.push([startIdx + netCharDiff, endIdx + netCharDiff]);
            return;
        }

        // Default case: range exists after net changes. There is no need to adjust
        updatedHighlights.push(highlightRange);
    });

    return hasChanged ? updatedHighlights : highlights;
}

/**
 * Strips away any emojis or other breaking special characters
 *
 * @param text plain text value
 */
export function sanitizeText(text: string): string {
    const noEmojis = text.replace(/(?!©|™|®|★|☆)\p{Extended_Pictographic}/gu, '');
    return noEmojis.replace(/\u200B|\r|[\b]|\f|\0|\v/g, '');
}

/**
 * Returns the top level node used in natural text editing, if any
 */
export function getRootTextContainerFromDOM(): HTMLDivElement | null {
    return document.querySelector(`#${TextEditingManager.rootContainerId}`);
}

/**
 * Returns the ContentEditable node for a text asset, if any, used for Natural Text Editing
 */
export function getTextInputFromDOM(): HTMLDivElement | null {
    return document.querySelector(`#${TextEditingManager.contentEditableId}`);
}

/**
 * Returns all textLine nodes rendered for a text asset, if any
 */
export function getTextLinesFromDOM(): Array<HTMLDivElement> | null {
    const textNode = getTextInputFromDOM();
    if (!textNode) {
        return null;
    }
    return Array.from(textNode.querySelectorAll(`.${TextEditingManager.textLineClass}`));
}

/**
 * Returns all wordElement nodes rendered for a text asset, if any, used for Natural Text Editing
 */
export function getWordElementsFromDOM(): Array<HTMLSpanElement> | null {
    const textNode = getTextInputFromDOM();
    if (!textNode) {
        return null;
    }

    // Ideally, each TextLine node will have a set of direct WordElement children nodes. However,
    // the contenteditable may mess things up when we delete or paste things by nesting things inside
    // of each other. Try and get only the relevant WordElement nodes, ignoring any duplicates and nested
    // nodes
    // One of the edge cases where contenteditable create just a text node and we don't use it to get final text.
    // to avoid this, we need to filter text nodes and replace them with span.word-element nodes.
    const filteredNodes = [];

    textNode.querySelectorAll(`.${TextEditingManager.textLineClass}`).forEach((textLine) => {
        if (textLine.hasChildNodes()) {
            textLine.childNodes.forEach((node) => {
                if (node.nodeType === Node.TEXT_NODE) {
                    // if child node is a text node we need to replace it with a span.word-element node
                    const newChild = document.createElement(`span`);
                    newChild.className = `${TextEditingManager.wordElementClass}`;
                    newChild.appendChild(node.cloneNode());
                    textLine.replaceChild(newChild, node);
                }
            });
        }
    });

    textNode.querySelectorAll(`.${TextEditingManager.textLineClass} > *`).forEach((node) => {
        if (node.childElementCount <= 1) {
            filteredNodes.push(node);
        } else {
            const childNodes = node.children;
            if (!childNodes.length) {
                filteredNodes.push(node);
            } else {
                Array.from(childNodes)
                    .filter((node) => node !== null)
                    .forEach((nestedNode) => {
                        filteredNodes.push(nestedNode);
                    });
            }
        }
    });
    return filteredNodes;
}

/**
 * If the text input is in an empty state, returns back the placeholder node that holds
 * the selection
 */
export function getPlaceholderFromDOM(): HTMLElement | null {
    const textNode = getTextInputFromDOM();
    if (!textNode) {
        return null;
    }
    return textNode.querySelector(`#${TextEditingManager.placeholderId}`);
}

/**
 * Based on the current selection, calculates and returns a new NodeAndOffset pairing to represent
 * where the cursor should be after pressing the Up or Down arrow key
 */
export function getSelectionForVerticalTraverse(
    direction: 'ArrowDown' | 'ArrowUp',
    textLinesForEditing: TextLineMetrics[],
    currentText: string,
): NodeAndOffset {
    // Considering a collapsed and non collapsed selection, choose either the focus or anchor to work
    // with depending on arrow direction
    const selection = window.getSelection();
    let currNode;
    let currOffset;
    if (selection.isCollapsed || direction === 'ArrowUp') {
        currNode = selection.anchorNode;
        currOffset = selection.anchorOffset;
    } else {
        currNode = selection.focusNode;
        currOffset = selection.focusOffset;
    }
    const { node: selectedNode, offset: selectedOffset } = getTextSelectionFromNode(
        currNode,
        currOffset,
    );

    // Determine the line the user is on
    const textLineNodes = getTextLinesFromDOM();
    const textLineIdx = textLineNodes.findIndex((textLineNode) =>
        textLineNode.contains(selectedNode),
    );
    if (textLineIdx === -1) {
        console.error('This node is not part of any line: ', selectedNode);
        throw new Error('The currentSelectedNode is not part of any line');
    }
    const currTextLine = textLinesForEditing[textLineIdx];

    // Handle edge cases where we are at either the first/bottom line
    if (textLineIdx === 0 && direction === 'ArrowUp') {
        const { anchorNode: node, anchorOffset: offset } = getSelectionFromCharRange([0, 0]);
        return { node, offset };
    } else if (textLineIdx === textLineNodes.length - 1 && direction === 'ArrowDown') {
        const textLen = currentText.length;
        const { anchorNode: node, anchorOffset: offset } = getSelectionFromCharRange([
            textLen,
            textLen,
        ]);
        return { node, offset };
    }

    // Complicated part: find the correct offset in the new line

    // 1. Find the corresponding wordElement
    const closestHtmlElement =
        selectedNode.nodeType === NODE_TYPE_TEXT
            ? selectedNode.parentElement
            : (selectedNode as HTMLElement);
    const wordEleNode = closestHtmlElement.closest(`.${TextEditingManager.wordElementClass}`);
    const wordNodesForLine = textLineNodes[textLineIdx].querySelectorAll(
        `.${TextEditingManager.wordElementClass}`,
    );
    const wordIdx = Array.from(wordNodesForLine).findIndex(
        (wordElementNode) => wordElementNode === wordEleNode,
    );

    const wordMetrics = currTextLine.words[wordIdx];
    const currWordBounds = wordMetrics.bounds;

    // 2. Find the next line to place cursor on
    const newLineIdx = direction === 'ArrowDown' ? textLineIdx + 1 : textLineIdx - 1;
    const newLineWords = textLinesForEditing[newLineIdx].words;

    // 3. Traverse words until we reach the closest x coord of the current word
    const currX =
        currWordBounds.x + (currWordBounds.width / wordMetrics.text.length) * selectedOffset;

    let nextWordIdx = 0;
    for (; nextWordIdx < newLineWords.length; nextWordIdx++) {
        const nextWordBounds = newLineWords[nextWordIdx].bounds;
        if (currX <= nextWordBounds.x) {
            break;
        }
    }
    // b/c the loop finds the first word element that starts PAST our initial position
    nextWordIdx = Math.max(0, nextWordIdx - 1);

    // 4. Determine character offset for the found word
    const nextWord = newLineWords[nextWordIdx];
    const nextWordBounds = nextWord.bounds;
    const nextWordEndX = nextWordBounds.x + nextWordBounds.width;

    let charOffset = 0;
    if (nextWordEndX < currX) {
        // the desired x position is further than the next word's end position
        // -----------
        // -------------⬆--
        charOffset = nextWord.text.length;
    } else if (currX <= nextWordBounds.x) {
        // the desired x position is before the next word's start position
        //         -----------
        // ---⬆--------------
        charOffset = 0;
    } else {
        const remainingXDiff = currX - nextWordBounds.x;
        const charWidth = nextWordBounds.width / nextWord.text.length;
        charOffset = charWidth === 0 ? 0 : Math.round(remainingXDiff / charWidth);
    }

    // 5. calculate a collapsed char range
    let finalCharOffset = 0;
    for (let i = 0; i < newLineIdx; i++) {
        finalCharOffset += textLinesForEditing[i].text.length;
    }
    for (let j = 0; j < nextWordIdx; j++) {
        finalCharOffset += newLineWords[j].text.length;
    }
    finalCharOffset += charOffset;

    // 6. Find the corresponding node from the char range and make sure it's on a different line
    let { anchorNode: newNode, anchorOffset: newOffset } = getSelectionFromCharRange([
        finalCharOffset,
        finalCharOffset,
    ]);
    const foundNodeLineIdx = textLineNodes.findIndex((lineNode) => lineNode.contains(newNode));
    if (foundNodeLineIdx !== newLineIdx) {
        // This is due to an edge case with trailing newlines and whitespaces
        // We will force the cursor to the beginning/end of the new calculated line
        const currLineBounds = currTextLine.bounds;
        const currLineMidpoint = currLineBounds.x + currLineBounds.width / 2;
        const forceCursorStart = currX <= currLineMidpoint;
        const newLineWordEleNodes = textLineNodes[newLineIdx].querySelectorAll(
            `.${TextEditingManager.wordElementClass}`,
        );

        if (forceCursorStart) {
            newNode = getContentNode(newLineWordEleNodes[0]);
            newOffset = 0;
        } else {
            newNode = getContentNode(newLineWordEleNodes[newLineWordEleNodes.length - 1]);
            newOffset = newNode.textContent.length;
        }
    }

    return { node: newNode, offset: newOffset };
}

/**
 * If there is currently a text asset in edit mode, this method will "stash" the
 * current window Selection, execute a function that produces side effects, and then
 * "pop" the selection back.
 *
 * Use this utility when state changes are being made in Luminary and the current selection
 * needs to be guaranteed to be maintained
 *
 * @example
 * restoreCursorAfterEffect(() => {
 *  slide.textPosition = VideoData.TOP_LEFT_TEXT_POSITION;
 *  textAsset.textSize = 50;
 * });
 *
 * @param callback a synchronous function
 */
export function restoreCursor(callback: Function) {
    const currSelectionAsTuple = getCharRangeFromSelection();
    if (currSelectionAsTuple === null) {
        callback();
        return;
    }

    callback();
    try {
        const selectionNodes = getSelectionFromCharRange(currSelectionAsTuple);
        // For canvas-based editing, getSelectionFromCharRange returns null because
        // there are no word element nodes. In this case, the canvas editing manager
        // handles selection restoration via #syncDomSelection, so we can skip this.
        if (selectionNodes === null) {
            return;
        }
        const { anchorNode, anchorOffset, focusNode, focusOffset } = selectionNodes;
        window.getSelection().setBaseAndExtent(anchorNode, anchorOffset, focusNode, focusOffset);
    } catch (e) {
        console.error(
            `Could not restore cursor back to original postion after performing luminary operation.
            ${JSON.stringify(currSelectionAsTuple)} was not valid.`,
        );
        console.error(e);
    }
}

/**
 * If the current selection is within the luminary controlled text editing input, converts the
 * current selection to a tuple containing the start and end indices of the text value
 *
 * @param readjustLineBreaks If true, and the selection is on a <br/> node, the char range
 * is shifted by 1 such that the range value is considered "in front of", or trailing the corresponding
 * newline character
 *
 * @return A tuple containing a start and an end index. If they are identical,
 *  that means the selection was collapsed
 */
export function getCharRangeFromSelection(readjustLineBreaks = false): CharTuple | null {
    if (getPlaceholderFromDOM() !== null) {
        console.warn('No range is applicable for the empty state.');
        return null;
    }
    const textInputNode = getTextInputFromDOM();
    if (!textInputNode) {
        console.error('No range can be calculated if the the text input does not exist.');
        return null;
    }
    const { focusNode, anchorNode, anchorOffset, focusOffset } = window.getSelection();
    if (!anchorNode || !textInputNode.contains(anchorNode)) {
        console.error(`
            The current selection is not within the text input. anchorNode: ${stringifyNode(anchorNode)},
            anchorOffset: ${anchorOffset}
        `);
        return null;
    }

    // 2. Try and determine which word elements each selection node corresponds to
    const wordElements = getWordElementsFromDOM();
    if (!wordElements || wordElements.length === 0) {
        return null;
    }
    const wordElementNodeList: Node[] = wordElements
        .map((span) => getContentNode(span))
        .filter((span) => span !== null);
    const anchorSelectionAsText = getTextSelectionFromNode(anchorNode, anchorOffset);
    const parsedAnchorNode = anchorSelectionAsText.node;
    const parsedAnchorOffset = anchorSelectionAsText.offset;

    const focusSelectionAsText = getTextSelectionFromNode(focusNode, focusOffset);
    const parsedFocusNode = focusSelectionAsText.node;
    const parsedFocusOffset = focusSelectionAsText.offset;

    let anchorNodeIdx = wordElementNodeList.indexOf(parsedAnchorNode);
    if (anchorNodeIdx === -1) {
        console.warn(
            'No valid word element index was found for',
            anchorNode,
            anchorOffset,
            anchorSelectionAsText,
        );
        anchorNodeIdx = 0;
    }
    let focusNodeIdx = wordElementNodeList.indexOf(parsedFocusNode);
    if (focusNodeIdx === -1) {
        console.warn(
            'No valid word element index was found for',
            focusNode,
            focusOffset,
            focusSelectionAsText,
        );
        focusNodeIdx = 0;
    }

    // 3. Convert "focus and anchor" to "start and end" nodes that are in document order
    const isFocusAndAnchorSame = anchorNodeIdx === focusNodeIdx;
    const isSelectionReverseOrder = isFocusAndAnchorSame
        ? parsedAnchorOffset > parsedFocusOffset
        : anchorNodeIdx > focusNodeIdx;
    const startHighlightNode = !isSelectionReverseOrder ? parsedAnchorNode : parsedFocusNode;
    const endHighlightNode = !isSelectionReverseOrder ? parsedFocusNode : parsedAnchorNode;
    const startNodeIndex = !isSelectionReverseOrder ? anchorNodeIdx : focusNodeIdx;
    const endNodeIndex = !isSelectionReverseOrder ? focusNodeIdx : anchorNodeIdx;

    // 4. Determine character offsets within the word element
    let startHighlightOffset = !isSelectionReverseOrder ? parsedAnchorOffset : parsedFocusOffset;
    let endHighlightOffset = !isSelectionReverseOrder ? parsedFocusOffset : parsedAnchorOffset;

    if (readjustLineBreaks) {
        // 4b. Edge case: if the start/end nodes are <br/> elements, for the purpose of generating a char range,
        // the offset should be 1, not 0, even though in the DOM that's technically not possible
        if (getContentNode(startHighlightNode).nodeName === 'BR') {
            startHighlightOffset = 1;
        }
        if (getContentNode(endHighlightNode).nodeName === 'BR') {
            endHighlightOffset = 1;
        }
    }

    // 5. Determine the selection's start and end index relative to the whole text
    const highlightStartInd =
        wordElementNodeList
            .slice(0, startNodeIndex)
            .reduce(
                (totalCount, wordElement) =>
                    totalCount + getContentFromWordElement(wordElement).length,
                0,
            ) + startHighlightOffset;

    let highlightEndInd;
    if (isFocusAndAnchorSame) {
        const offsetDiff = endHighlightOffset - startHighlightOffset;
        highlightEndInd = highlightStartInd + offsetDiff;
    } else {
        const tailAnchorCharLen =
            getContentFromWordElement(startHighlightNode).length - startHighlightOffset;
        let charLenBetweenNodes = 0;
        if (endNodeIndex - startNodeIndex - 1 > 0) {
            // they're not adjactent
            charLenBetweenNodes = wordElementNodeList
                .slice(startNodeIndex + 1, endNodeIndex)
                .reduce(
                    (totalCount, wordElement) =>
                        totalCount + getContentFromWordElement(wordElement).length,
                    0,
                );
        }
        highlightEndInd =
            highlightStartInd + tailAnchorCharLen + charLenBetweenNodes + endHighlightOffset;
    }

    return [highlightStartInd, highlightEndInd];
}

/**
 * If the current selection is within the luminary controlled text editing input,
 * returns the base and extent nodes (with their respsective offsets) of the
 * input's word elements from the given highlight range
 *
 * @param readjustLineBreaks if the range falls on the boundary of a newline and another word, the selection
 *  can exist on the line break element itself, or the following node. By default, this parameter is true, meaning
 *  in this scenario the selection returned will be of the following node.
 */
export function getSelectionFromCharRange(
    highlightRange: CharTuple,
    readjustLineBreaks: boolean = true,
): AnchorAndFocus | null {
    if (getPlaceholderFromDOM() !== null) {
        console.warn('No selection is valid for the empty state.');
        return null;
    }
    const wordElementNodeList = getWordElementsFromDOM();
    if (!wordElementNodeList?.length) {
        return null;
    }

    const fullTextLength = getContentFromInput().length;
    const isStartIndexInvalid = highlightRange[0] < 0 || highlightRange[0] > fullTextLength;
    const isEndIndexInvalid = highlightRange[1] < 0 || highlightRange[1] > fullTextLength;
    if (isStartIndexInvalid || isEndIndexInvalid) {
        console.error(`
            The range given ${highlightRange} is out of bounds compared to the
            current rendered text range [0, ${fullTextLength}]
        `);
        return null;
    }

    const isSelectionCollapsed = highlightRange[0] === highlightRange[1];
    let anchorNode, anchorOffset, focusNode, focusOffset;

    // Iterate every word element DOM node (left to right) and count its text content until we find
    // the given highlight range. This loop biases against the first of two consecutive nodes if the
    // range is at the boundary of two nodes.
    let totalCharCount = 0;
    let anchorFound = false;
    let focusFound = isSelectionCollapsed;
    let wordEleIdx = 0;
    while ((!anchorFound || !focusFound) && wordEleIdx < wordElementNodeList.length) {
        const wordEleNode = wordElementNodeList[wordEleIdx];
        const wordEleText = getContentFromWordElement(wordEleNode);

        const getSelectionFromWordEle = (
            wordEleNode: HTMLSpanElement,
            charOffset: number,
        ): NodeAndOffset => {
            let node = getContentNode(wordEleNode);
            let offset = 0;

            // Special case: if the found node is a line break, the cursor can exist either on the break node itself
            // or the following node at the start position
            if (node.nodeName === 'BR') {
                if (readjustLineBreaks && wordElementNodeList[wordEleIdx + 1]) {
                    node = getContentNode(wordElementNodeList[wordEleIdx + 1]);
                }
            } else {
                offset = charOffset - totalCharCount;
            }

            return { node, offset };
        };

        if (!anchorFound && highlightRange[0] <= totalCharCount + wordEleText.length) {
            const { node, offset } = getSelectionFromWordEle(wordEleNode, highlightRange[0]);
            anchorNode = node;
            anchorOffset = offset;
            anchorFound = true;
        }
        if (!focusFound && highlightRange[1] <= totalCharCount + wordEleText.length) {
            const { node, offset } = getSelectionFromWordEle(wordEleNode, highlightRange[1]);
            focusNode = node;
            focusOffset = offset;
            focusFound = true;
        }

        totalCharCount += wordEleText.length;
        wordEleIdx++;
    }
    if (isSelectionCollapsed) {
        focusNode = anchorNode;
        focusOffset = anchorOffset;
    }

    return { anchorNode, anchorOffset, focusNode, focusOffset };
}

/**
 * Constructs a text value from the luminary input, accounting for reset whitespaces
 * and newlines
 */
export function getContentFromInput(): string | null {
    const wordElementList = getWordElementsFromDOM();
    if (wordElementList === null) {
        return null;
    }
    return wordElementList.reduce((str, wordEle) => str + getContentFromWordElement(wordEle), '');
}

/**
 * Returns the text value from an HTML element, which may be one of any nodes generated by
 * TextElementBuilder or one generated by the native contenteditable logic
 */
function getContentFromWordElement(wordElement: Node): string {
    if (wordElement.nodeType === NODE_TYPE_TEXT) {
        return wordElement.textContent;
    }

    const htmlElement = wordElement as HTMLElement;
    if ((wordElement as HTMLElement).nodeName === 'BR') {
        // A line break element should be backconverted to a newline
        return '\n';
    } else if (htmlElement.dataset.text && wordElement.childNodes.length === 1) {
        // In TextElementBuilder, whitespaces and line breaks are contained by a parent
        // with a dataset.text value and exactly one node child (either text or a <br/> element)
        return htmlElement.dataset.text;
    } else if (htmlElement.childElementCount === 0 && htmlElement.textContent.length) {
        // The node contains just text and no other children
        return htmlElement.textContent;
    } else if (wordElement.childNodes.length > 1) {
        // This shouldn't typically happen but the contenteditable may merge together nodes
        // such that it could have both text and some other HTML element. In this case, recursively
        // build the contents from each child
        return Array.from(wordElement.childNodes).reduce((str, child) => {
            return str + getContentFromWordElement(child);
        }, '');
    } else {
        return '';
    }
}

/**
 * Given a node, recursively visits its children to return either a word element's text content
 * or line break element. Depending on the caller, the function either biases towards looking at
 * the node's first child or last child (recursively).
 */
function getContentNode(
    element: Node,
    traversalDir: NodeTraversalDirection = NodeTraversalDirection.LastChild,
): Node | null {
    if (!element) {
        return null;
    }
    if (element.nodeType === NODE_TYPE_TEXT) {
        return element;
    }
    const htmlElement = element as HTMLElement;
    if (htmlElement.nodeName === 'BR') {
        return element;
    }
    if (htmlElement.classList.contains(TextEditingManager.wordElementClass)) {
        return getContentNode(htmlElement.firstChild, traversalDir);
    }

    const childIdx =
        traversalDir === NodeTraversalDirection.LastChild ? htmlElement.childElementCount - 1 : 0;
    return getContentNode(htmlElement.children[childIdx], traversalDir);
}

/**
 * Given a node and offset from the window selection, makes sure that the returned node and offset
 * corresponds to a node that is either the text content of a word element, or a line break element.
 */
function getTextSelectionFromNode(node: Node, offset: number): NodeAndOffset {
    if (node.nodeType === NODE_TYPE_TEXT || node.nodeName === 'BR') {
        return { node, offset };
    }
    const htmlEle = node as HTMLElement;

    let parsedNode;
    let modifiedOffset;
    if (offset === 0) {
        const targetNode = htmlEle.children[offset];
        parsedNode = getContentNode(targetNode, NodeTraversalDirection.FirstChild);
        modifiedOffset = 0;
    } else if (offset >= htmlEle.childElementCount) {
        const targetNode = htmlEle.children[htmlEle.childElementCount - 1];
        parsedNode = getContentNode(targetNode, NodeTraversalDirection.LastChild);
        modifiedOffset = parsedNode.textContent.length;
    } else {
        const targetNode = htmlEle.children[offset - 1];
        parsedNode = getContentNode(targetNode, NodeTraversalDirection.LastChild);
        modifiedOffset = parsedNode.textContent.length;
    }

    return {
        node: parsedNode,
        offset: modifiedOffset,
    };
}

/**
 * Used for debuggiung purposes. Outputs the node as a string
 */
function stringifyNode(node: Node | HTMLElement): string {
    let str;
    try {
        if (node.nodeType === NODE_TYPE_TEXT) {
            str = node.parentElement.outerHTML;
        }
        str = (node as HTMLElement).outerHTML;
    } catch (e) {
        console.error("Couldn't stringify node:", node, e);
        str = node;
    }
    return str;
}

/**
 * In addition to Luminary.referenceResolution scaling, the canvas might scaled so that one luminary pixel is
 * not equivalent to a dom pixel.
 */
export function getRootContainerScale(luminary: Luminary, canvasWidth: number): number {
    if (canvasWidth === 0) {
        // Width not known (yet). Simulate scale of 1.
        return 1.0;
    }

    return canvasWidth / luminary.referenceResolution.width;
}
